import { IconButton, TooltipHost } from '@fluentui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalState } from '../../Redux/RootReducer';
import { WalkieTalkieActions } from '../../Redux/WalkieTalkie/WalkieTalkieAction';
import audioUtil from '../../Utils/AudioService';
import walkieTalkieTimer from '../../Utils/WalkieTalkieTimer';
import './WalkieTalkieControls.scss';

// ===== Robust Audio State Machine =====
enum AudioState {
  IDLE = 'IDLE',                                     
  LISTENING = 'LISTENING',                           
  TRANSITIONING_TO_TX = 'TRANSITIONING_TO_TX',       
  TRANSMITTING = 'TRANSMITTING',                     
  TRANSITIONING_TO_RX = 'TRANSITIONING_TO_RX',       
}

export interface IWalkieTalkieControlsProps {
  sentinelId: number; 
  disabled?: boolean; 
  compactMode?: boolean; 
  activatedBy?: 'alarm' | 'manual'; 
  // pulsanti grigi con questo suggerimento (es. il dispositivo non risponde)
  unavailableLabel?: string;
}

const WalkieTalkieControls = (props: IWalkieTalkieControlsProps) => {
  const dispatch = useDispatch();

  // Redux State
  const walkieTalkie = useSelector((state: GlobalState) => state.walkieTalkie);
  const { activeChannelSentinelId, isTalking, isListening, timerDuration} = walkieTalkie;

  const isActive = activeChannelSentinelId === props.sentinelId;
  const isDisabled = props.disabled || (activeChannelSentinelId !== null && !isActive);

  // ===== Local State Machine with Synchronous Refs =====
  const [audioState, setAudioState] = useState<AudioState>(AudioState.IDLE);
  const audioStateRef = useRef<AudioState>(AudioState.IDLE);

  const [isLocked, setIsLocked] = useState<boolean>(false);
  const isLockedRef = useRef<boolean>(false);

  // Helpers to keep State and Refs strictly in sync
  const updateAudioState = useCallback((newState: AudioState) => {
    audioStateRef.current = newState;
    setAudioState(newState);
  }, []);

  const updateIsLocked = useCallback((locked: boolean) => {
    isLockedRef.current = locked;
    setIsLocked(locked);
  }, []);

  // ===== Smart PTT System =====
  const lastPressTime = useRef<number>(0);
  const pressDownTime = useRef<number>(0);
  const lastClickTime = useRef<number>(0);
  const transmissionTimeoutId = useRef<number | null>(null); 
  const DEBOUNCE_MS = 500; 
  const HOLD_THRESHOLD_MS = 250; 
  const DOUBLE_CLICK_MS = 400; 

  /**
   * Cleans up local state aggressively when channel goes inactive
   */
  useEffect(() => {
    if (!isActive) {
      updateIsLocked(false);
      updateAudioState(AudioState.IDLE);
      
      if (transmissionTimeoutId.current !== null) {
        clearTimeout(transmissionTimeoutId.current);
        transmissionTimeoutId.current = null;
      }
    }
  }, [isActive, updateIsLocked, updateAudioState]);

  const handleToggleChannel = async () => {
    if (isActive) {
      console.log(`🔴 WalkieTalkieControls: Shutting down channel ${props.sentinelId}`);

      if (transmissionTimeoutId.current !== null) {
        clearTimeout(transmissionTimeoutId.current);
        transmissionTimeoutId.current = null;
      }

      walkieTalkieTimer.stop();
      audioUtil.closeDeviceMic(props.sentinelId);
      audioUtil.disableAudioStream(props.sentinelId);

      dispatch(WalkieTalkieActions.disableAllChannels());
      updateAudioState(AudioState.IDLE);
      updateIsLocked(false);
    } else {
      console.log(`🟢 WalkieTalkieControls: Activating channel ${props.sentinelId}`);

      if (activeChannelSentinelId !== null) {
        audioUtil.closeDeviceMic(activeChannelSentinelId);
        audioUtil.disableAudioStream(activeChannelSentinelId);
        walkieTalkieTimer.stop();
      }

      dispatch(
        WalkieTalkieActions.setActiveChannel(
          props.sentinelId,
          props.activatedBy || 'manual'
        )
      );

      audioUtil.openDeviceMic(props.sentinelId);
      walkieTalkieTimer.start(dispatch, props.sentinelId, timerDuration);
      updateAudioState(AudioState.LISTENING);
    }
  };

  const startTransmission = useCallback(async () => {
    console.log(`🎙️ WalkieTalkieControls: Starting transmission ${props.sentinelId}`);
    updateAudioState(AudioState.TRANSITIONING_TO_TX);

    try {
      const closeSuccess = await audioUtil.closeDeviceMicAsync(props.sentinelId);
      if (!closeSuccess) {
        console.warn('⚠️ closeDeviceMic failed, continuing anyway...');
      }

      const openSuccess = await audioUtil.enableAudioStreamAsync(props.sentinelId);
      if (!openSuccess) {
        throw new Error('enableAudioStream failed');
      }

      updateAudioState(AudioState.TRANSMITTING);
      dispatch(WalkieTalkieActions.startTalking());
      walkieTalkieTimer.stop();
    } catch (error) {
      console.error('❌ Error during transmission startup:', error);
      updateAudioState(AudioState.LISTENING);
    }
  }, [props.sentinelId, dispatch, timerDuration, updateAudioState]);

  const stopTransmission = useCallback(async () => {
    console.log(`🔇 WalkieTalkieControls: Ending transmission ${props.sentinelId}`);
    updateAudioState(AudioState.TRANSITIONING_TO_RX);

    try {
      const closeSuccess = await audioUtil.disableAudioStreamAsync(props.sentinelId);
      if (!closeSuccess) {
        console.warn('⚠️ disableAudioStream failed, continuing anyway...');
      }

      const openSuccess = await audioUtil.openDeviceMicAsync(props.sentinelId);
      if (!openSuccess) {
        throw new Error('openDeviceMic failed');
      }

      updateAudioState(AudioState.LISTENING);
      dispatch(WalkieTalkieActions.stopTalking());
      walkieTalkieTimer.start(dispatch, props.sentinelId, timerDuration);
    } catch (error) {
      console.error('❌ Error during transmission stop:', error);
      updateAudioState(AudioState.IDLE);
    }
  }, [props.sentinelId, dispatch, timerDuration, updateAudioState]);

  // Dell'evento serve solo preventDefault: il tipo minimo evita di legarsi a quello del bottone.
  const handleTalkStart = useCallback(async (e: { preventDefault(): void }) => {
    if (!isActive) return;

    // Prevent default touch behaviors if triggered by touch
    e.preventDefault(); 
    
    const now = Date.now();

    // MODE 2: DOUBLE CLICK (Toggle Lock)
    if (now - lastClickTime.current < DOUBLE_CLICK_MS) {
      console.log('🔒 Double click detected - Toggling lock mode');

      if (transmissionTimeoutId.current !== null) {
        clearTimeout(transmissionTimeoutId.current);
        transmissionTimeoutId.current = null;
      }

      if (isLockedRef.current) {
        updateIsLocked(false);
        await stopTransmission();
      } else {
        if (audioStateRef.current !== AudioState.TRANSMITTING) {
          updateIsLocked(true);
          await startTransmission();
        }
      }
      lastClickTime.current = 0; 
      return;
    }

    lastClickTime.current = now;

    if (isLockedRef.current) {
      console.log('ℹ️ Lock mode is active, use double click to unlock');
      return;
    }

    if (now - lastPressTime.current < DEBOUNCE_MS) {
      console.log('🚫 PTT debounced (too fast)');
      return;
    }

    if (audioStateRef.current === AudioState.TRANSITIONING_TO_TX ||
        audioStateRef.current === AudioState.TRANSMITTING) {
      console.warn('⚠️ Already transmitting or transitioning');
      return;
    }

    // MODE 1: HOLD-TO-TALK
    pressDownTime.current = now;
    lastPressTime.current = now;

    console.log(`⏱️ PTT pressed, waiting ${HOLD_THRESHOLD_MS}ms before starting transmission...`);

    transmissionTimeoutId.current = window.setTimeout(async () => {
      console.log('✅ Hold threshold reached, starting transmission...');
      transmissionTimeoutId.current = null;
      await startTransmission();
    }, HOLD_THRESHOLD_MS);
  }, [isActive, startTransmission, stopTransmission, updateIsLocked]);

  const handleTalkEnd = useCallback(async (e: { preventDefault(): void }) => {
    if (!isActive) return;
    e.preventDefault();

    // CRITICAL FIX: Evaluate the Ref, not the State, to avoid race conditions
    if (isLockedRef.current) {
      console.log('ℹ️ Lock mode active, ignoring release');
      return;
    }

    const now = Date.now();
    // Ensure we don't calculate massive durations if pressDownTime was never set
    const pressDuration = pressDownTime.current === 0 ? 0 : now - pressDownTime.current;

    // MODE 1: HOLD-TO-TALK (Duration Check)
    if (pressDuration < HOLD_THRESHOLD_MS) {
      console.log(`⏱️ PTT released too early (${pressDuration}ms < ${HOLD_THRESHOLD_MS}ms)`);

      if (transmissionTimeoutId.current !== null) {
        clearTimeout(transmissionTimeoutId.current);
        transmissionTimeoutId.current = null;
        console.log('🚫 Transmission timer cancelled - no action required');
        pressDownTime.current = 0;
        return;
      }

      if (audioStateRef.current === AudioState.TRANSMITTING || audioStateRef.current === AudioState.TRANSITIONING_TO_TX) {
        console.log('🔄 Restoring device listening after rapid release...');
        updateAudioState(AudioState.TRANSITIONING_TO_RX);

        try {
          await audioUtil.disableAudioStreamAsync(props.sentinelId);
          await audioUtil.openDeviceMicAsync(props.sentinelId);

          updateAudioState(AudioState.LISTENING);
          dispatch(WalkieTalkieActions.stopTalking());
          walkieTalkieTimer.start(dispatch, props.sentinelId, timerDuration);
          console.log('✅ Device listening restored');
        } catch (error) {
          console.error('❌ Error restoring listening state:', error);
          updateAudioState(AudioState.IDLE);
        }
      }

      pressDownTime.current = 0;
      return;
    }

    console.log(`✅ Valid hold-to-talk (${pressDuration}ms), stopping transmission`);

    if (audioStateRef.current !== AudioState.TRANSMITTING) {
      console.warn('⚠️ Not transmitting, ignoring handleTalkEnd');
      return;
    }

    await stopTransmission();
    pressDownTime.current = 0;
  }, [isActive, stopTransmission, props.sentinelId, dispatch, timerDuration, updateAudioState]);

  useEffect(() => {
    return () => {
      if (transmissionTimeoutId.current !== null) {
        clearTimeout(transmissionTimeoutId.current);
        transmissionTimeoutId.current = null;
      }

      if (isActive) {
        walkieTalkieTimer.stop();
        audioUtil.closeDeviceMic(props.sentinelId);
        audioUtil.disableAudioStream(props.sentinelId);
      }
    };
  }, [isActive, props.sentinelId]);

  // suggerimenti di sempre, tradotti in italiano
  const unavailable = !isActive ? props.unavailableLabel : undefined;
  const channelLabel = unavailable ?? (isActive ? 'Disattiva canale' : 'Attiva canale');
  const talkLabel = unavailable ?? (!isActive
    ? 'Canale spento'
    : isLocked
      ? '🔒 Bloccato - Doppio clic per sbloccare'
      : isTalking
        ? 'Trasmissione attiva | Rilascia per terminare'
        : 'Tieni premuto 500 ms per parlare | Doppio clic per bloccare');
  // finestra dell'allarme (compatta): pulsanti larghi 3em come sempre
  const cellStyle = props.compactMode ? { width: '3em' } : undefined;

  return (
    <div className={`walkie-talkie-controls ${props.compactMode ? 'compact' : ''}`}>
      <div style={cellStyle} className="center-content">
        <TooltipHost content={channelLabel}>
          <IconButton
            iconProps={{ iconName: 'Volume2' }}
            disabled={(isDisabled || !!unavailable) && !isActive}
            allowDisabledFocus={!!unavailable}
            onClick={handleToggleChannel}
            ariaLabel={channelLabel}
            className={`walkie-button ${isActive ? 'active' : 'inactive'}`}
          />
        </TooltipHost>
      </div>

      <div style={cellStyle} className="center-content">
        <TooltipHost content={talkLabel}>
          <IconButton
            iconProps={{ iconName: isLocked ? 'LockSolid' : 'Microphone' }}
            disabled={!isActive}
            allowDisabledFocus={!!unavailable}
            ariaLabel={talkLabel}
            // CRITICAL FIX: Unified pointer events
            // I gestori sono async: l'IconButton vuole handler che restituiscono void, quindi la
            // Promise si scarta esplicitamente (gli errori li gestiscono gia' i try/catch interni).
            onPointerDown={(e) => { void handleTalkStart(e); }}
            onPointerUp={(e) => { void handleTalkEnd(e); }}
            onPointerLeave={(e) => { void handleTalkEnd(e); }}
            className={`walkie-button talk-button ${isTalking && isActive ? 'talking' : ''} ${isLocked ? 'locked' : ''}`}
          />
        </TooltipHost>
      </div>
    </div>
  );
};

export default WalkieTalkieControls;